import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { allorders, createOrder, retryPayment, verifyPayment } from "../Redux/Slice/Payment.slice";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeCheckoutForm from "../component/StripeCheckoutForm";
import OrderSuccessModal from "../component/OrderSuccessModal";
import axiosInstance from "../Utils/axiosInstance";
import { Modal } from "@mui/material";
import { motion } from "framer-motion";
import { decryptData } from "../Utils/encryption";
import { useNavigate } from "react-router-dom";
import { generateInvoice } from "../Utils/invoiceGenerator";
import { FaDownload } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { toast } from "react-hot-toast";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Orders = ({ viewMode }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Orders • YOYO";
    window.scrollTo(0, 0);
  }, []);

  const allOrders = useSelector((state) => state.payment.orders)

  const user = useSelector((state) => state.auth.user);
  const [showMoreFor, setShowMoreFor] = useState(null);
  // const [viewMode, setViewMode] = useState("card"); // 'card' or 'list'
  const [showAllItemsForOrder, setShowAllItemsForOrder] = useState(null); // New state to control showing all items for a specific order
  const [processingOrderId, setProcessingOrderId] = useState(null)
  const [invoiceModal, setInvoiceModal] = useState({ open: false, order: null });
  const [openStripeModal, setOpenStripeModal] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const indexOfLastOrder = currentPage * itemsPerPage;
  const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
  const currentOrders = allOrders ? allOrders.slice(indexOfFirstOrder, indexOfLastOrder) : [];
  const totalPages = allOrders ? Math.ceil(allOrders.length / itemsPerPage) : 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [allOrders?.length]);

  // Helper to format date
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleViewInvoice = (order) => {
    setInvoiceModal({ open: true, order: order });
  };

  const closeInvoiceModal = () => {
    setInvoiceModal({ open: false, order: null });
  };

  const downloadFromModal = async () => {
    if (invoiceModal.order) {
      try {
        const userDetails = {
          name: user?.fullName ? decryptData(user.fullName) : "Customer",
          email: user?.email ? decryptData(user.email) : "",
        };
        await generateInvoice(invoiceModal.order, userDetails, 'save');
      } catch (error) {
        console.error("Error generating invoice for download:", error);
      }
    }
  };

  const fadeUp = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
    },
  };

  // Helper functions for directional hover effect
  const getDirection = (e, element) => {
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;

    const top = y;
    const right = w - x;
    const bottom = h - y;
    const left = x;

    const min = Math.min(top, right, bottom, left);

    if (min === top) return "top";
    if (min === right) return "right";
    if (min === bottom) return "bottom";
    return "left";
  };

  const mapDirToTransform = (dir) => {
    const map = {
      top: "translate3d(0, -100%, 0)",
      right: "translate3d(100%, 0, 0)",
      bottom: "translate3d(0, 100%, 0)",
      left: "translate3d(-100%, 0, 0)",
    };
    return map[dir];
  };

  // HoverCard component
  function HoverCard({
    className = "",
    children,
    roundedClass = "rounded-xl",
  }) {
    const cardRef = useRef(null);
    const isLeavingRef = useRef(false);
    const enterRafRef = useRef(0);
    const [overlayTransform, setOverlayTransform] = useState(
      "translate3d(0, 0, 0)"
    );
    const [overlayOpacity, setOverlayOpacity] = useState(0);

    const handleEnter = (e) => {
      const dir = getDirection(e, cardRef.current);
      isLeavingRef.current = false;
      setOverlayOpacity(1);
      setOverlayTransform(mapDirToTransform(dir));
      if (enterRafRef.current) cancelAnimationFrame(enterRafRef.current);
      enterRafRef.current = requestAnimationFrame(() => {
        setOverlayTransform("translate3d(0, 0, 0)");
        enterRafRef.current = 0;
      });
    };

    const handleLeave = (e) => {
      const dir = getDirection(e, cardRef.current);
      isLeavingRef.current = true;
      if (enterRafRef.current) {
        cancelAnimationFrame(enterRafRef.current);
        enterRafRef.current = 0;
      }
      setOverlayOpacity(0);
      setOverlayTransform(mapDirToTransform(dir));
    };

    const handleTransitionEnd = () => {
      if (isLeavingRef.current) {
        setOverlayOpacity(0);
        isLeavingRef.current = false;
      }
    };

    useEffect(() => {
      return () => {
        if (enterRafRef.current) cancelAnimationFrame(enterRafRef.current);
      };
    }, []);

    return (
      <div
        ref={cardRef}
        onPointerEnter={handleEnter}
        onPointerLeave={handleLeave}
        className={`relative overflow-hidden ${roundedClass} ${className}`}
      >
        <div
          className={`pointer-events-none absolute inset-0 ${roundedClass} bg-white/10 border border-transparent transition-all duration-300 ease-out`}
          style={{
            transform: overlayTransform,
            opacity: overlayOpacity,
            willChange: "transform, opacity",
            zIndex: 1,
          }}
          onTransitionEnd={handleTransitionEnd}
        />
        {children}
      </div>
    );
  }

  const handleRetryPayment = async (order) => {
    setProcessingOrderId(order._id)
    if (process.env.REACT_APP_PAYMENT_TYPE === "razorpay") {
      const resultAction = await dispatch(createOrder({ amount: order?.amount }));
      if (createOrder.fulfilled.match(resultAction)) {
        const { razorpayOrder } = resultAction.payload;
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Yoyo",
          description: "Game Purchase",
          order_id: razorpayOrder.id,
          image: "/logo556.png",
          handler: function (response) {
            console.log(response, "response");

            dispatch(verifyPayment({
              items: order.items,
              amount: order.amount,
              paymentResponse: response,
              buyNow: false,
              orderId: order._id
            })).then((res) => {
              if (verifyPayment.fulfilled.match(res)) {
                toast.success("Payment successful!");
                try {
                  const updatedOrder = res.payload.order;
                  const userDetails = {
                    name: user?.fullName ? decryptData(user.fullName) : "Customer",
                    email: user?.email ? decryptData(user.email) : "",
                  };
                  generateInvoice(updatedOrder, userDetails, "save");
                  setShowSuccessModal(true);
                } catch (e) {
                  console.error("Error auto-opening invoice", e);
                }
              }
            });
          },
          prefill: {
            name: decryptData(user.fullName),
            email: decryptData(user.email),
          },
          theme: {
            color: "#7c3aed",
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
        setProcessingOrderId(null)
      }
    } else {
      // Stripe flow
      try {
        const response = await axiosInstance.post("/order/stripe/create-payment-intent", {
          amount: order?.amount,
        });

        if (response.data.success) {
          setClientSecret(response.data.clientSecret);
          setOrderData({ items: order.items, amount: order?.amount, orderId: order._id });
          setOpenStripeModal(true);
        }
      } catch (err) {
        console.error("Stripe retry error:", err);
      } finally {
        setProcessingOrderId(null);
      }
    }
  };

  useEffect(() => {
    dispatch(allorders());
  }, []);

  if (!allOrders || allOrders.length === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
        <div className="text-2xl md:text-3xl font-semibold text-white mb-2">
          You have no orders yet
        </div>
        <div className="text-white/60 mb-6">
          Explore our collection and add some games to your cart.
        </div>
        <button
          onClick={() => navigate("/games")}
          className="px-6 py-3 rounded-md bg-gradient-primary transition-all"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  // console.log(allOrders,"allOrders");


  return (
    <>
      <div className="bg-[#1b1920]/60 backdrop-blur-xl border border-white/10 text-white p-4 sm:p-6 rounded-2xl relative shadow-2xl">
        {/* Conditional Rendering */}
        {viewMode === "card" ? (
          <motion.section
            variants={fadeUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="grid xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-1 md600:grid-cols-2 grid-cols-1 gap-6"
          >
            {currentOrders.map((order, i) => {
              const isPaid = order.status === "paid";
              return (
                <motion.div
                  key={order._id}
                  variants={fadeUp}
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                >
                  <HoverCard className="group relative border border-white/10 bg-[#16141a]/40 py-5 px-6 transition duration-300 ease-out hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-purple-500/10 backdrop-blur-md h-72 flex flex-col justify-between rounded-2xl">

                    <div className="relative z-10 flex flex-col h-full justify-between">
                      {/* Order Header */}
                      <div className="flex justify-between items-start">
                        <div>
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${isPaid
                              ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.1)]"
                              : order.status === "failed"
                                ? "bg-rose-500/10 border-rose-500/20 text-rose-400 shadow-[0_0_10px_rgba(244,63,94,0.1)]"
                                : "bg-amber-500/10 border-amber-500/20 text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.1)]"
                              }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${isPaid ? "bg-emerald-400" : order.status === "failed" ? "bg-rose-400" : "bg-amber-400 animate-pulse"}`} />
                            {order.status}
                          </span>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1">
                          <div className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300 font-extrabold text-xl font-mono">
                            ${order?.amount?.toFixed(2)}
                          </div>
                          <div className="text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                            {order.currency}
                          </div>
                        </div>
                      </div>

                      {/* Order Info */}
                      <div className="my-3">
                        <h3 className="text-white font-bold tracking-wide text-sm sm:text-base group-hover:text-purple-400 transition-colors duration-300">
                          {order?.orderNumber ? order?.orderNumber : `Order #${order?._id?.slice(-8).toUpperCase()}`}
                        </h3>
                        <p className="text-gray-400 text-xs mt-0.5">
                          {formatDate(order.createdAt)}
                        </p>
                      </div>

                      {/* Items Preview */}
                      <div className="space-y-3 my-2 max-h-24 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-purple-500/40 scrollbar-track-transparent">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-white/[0.02] hover:bg-white/[0.04] p-1.5 rounded-xl border border-white/[0.04] transition-all duration-300">
                            <img
                              src={item?.game?.cover_image?.url}
                              alt={item.game?.title}
                              className="w-10 h-10 object-cover rounded-lg border border-white/10 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-white text-xs sm:text-sm truncate">
                                {item.game.title}
                              </div>
                              <div className="text-[10px] text-purple-400 font-medium capitalize mt-0.5">
                                {item.platform}
                              </div>
                            </div>
                            <div className="font-bold text-gray-300 text-xs font-mono shrink-0">
                              ${item.price}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Footer Info & Actions */}
                      <div className="border-t border-white/10 pt-3 flex justify-between items-center mt-2">
                        {isPaid ? (
                          <>
                            <div className="text-[11px] text-gray-400 font-mono truncate max-w-[150px]">
                              ID: {order.razorpayOrderId || order.stripePaymentIntentId || "N/A"}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewInvoice(order);
                                }}
                                className="flex items-center gap-1 text-[11px] font-bold text-purple-300 hover:text-purple-200 transition-colors uppercase tracking-wider"
                                title="View Invoice"
                              >
                                <FaDownload size={10} /> Invoice
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/profile/downloads');
                                }}
                                className="flex items-center gap-1 text-[11px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider"
                                title="Go to Downloads"
                              >
                                <FaDownload size={10} /> Download
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="w-full flex justify-end">
                            <button
                              className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 cursor-pointer"
                              onClick={() => handleRetryPayment(order)}
                              disabled={processingOrderId === order._id}
                            >
                              {processingOrderId === order._id ? (
                                <span className="flex items-center gap-1.5">
                                  <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                  </svg>
                                  Processing..
                                </span>
                              ) : order.status === "failed" ? "Retry Payment" : "Pay Now"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </HoverCard>
                </motion.div>
              );
            })}
          </motion.section>
        ) : (
          // List view
          <div className="overflow-x-auto rounded-2xl border border-white/10 h-full scrollbar-thin scrollbar-thumb-purple-500/20 scrollbar-track-transparent bg-[#16141a]/30">
            <table className="min-w-[950px] w-full divide-y divide-white/5 border-collapse">
              <thead>
                <tr className="bg-[#24222a] border-b border-white/10">
                  <th className="px-5 py-4 text-left text-[11px] font-extrabold text-purple-300 uppercase tracking-widest">
                    Order Ref
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-extrabold text-purple-300 uppercase tracking-widest">
                    Date
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-extrabold text-purple-300 uppercase tracking-widest">
                    Amount
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-extrabold text-purple-300 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-extrabold text-purple-300 uppercase tracking-widest">
                    Games List
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-extrabold text-purple-300 uppercase tracking-widest">
                    Transaction ID
                  </th>
                  <th className="px-5 py-4 text-center text-[11px] font-extrabold text-purple-300 uppercase tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {currentOrders.map((order, idx) => {
                  const isPaid = order.status === "paid";
                  return (
                    <motion.tr
                      key={order._id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.04 }}
                      className={`transition-colors hover:bg-purple-900/10 group ${idx % 2 === 0 ? "bg-white/[0.01]" : "bg-transparent"
                        }`}
                    >
                      <td className="px-5 py-4 font-mono text-white font-bold text-sm">
                        {order.orderNumber ? order.orderNumber : `#${order._id.slice(-8).toUpperCase()}`}
                      </td>
                      <td className="px-5 py-4 text-gray-300 text-sm">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-5 py-4 text-emerald-400 font-extrabold text-sm font-mono">
                        ${order?.amount?.toFixed(2)}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${isPaid
                            ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                            : order.status === "failed"
                              ? "bg-rose-500/10 border-rose-500/20 text-rose-400"
                              : "bg-amber-500/10 border-amber-500/20 text-amber-400"
                            }`}
                        >
                          <span className={`w-1 h-1 rounded-full ${isPaid ? "bg-emerald-400" : order.status === "failed" ? "bg-rose-400" : "bg-amber-400"}`} />
                          {order.status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1.5 max-w-[220px]">
                          {(showAllItemsForOrder === order._id
                            ? order.items
                            : order.items.slice(0, 1)
                          ).map((item, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-2 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] px-2 py-1 rounded-lg text-xs text-gray-200"
                            >
                              <img
                                src={item.game?.cover_image?.url}
                                alt={item.game.title}
                                className="w-5 h-5 object-cover rounded"
                              />
                              <span className="truncate">{item.game.title}</span>
                            </span>
                          ))}
                          {order.items.length > 1 &&
                            showAllItemsForOrder !== order._id && (
                              <button
                                className="text-[10px] text-purple-400 hover:text-purple-300 underline font-semibold cursor-pointer text-left self-start"
                                onClick={() => setShowAllItemsForOrder(order._id)}
                              >
                                +{order.items.length - 1} more items
                              </button>
                            )}
                          {order.items.length > 1 &&
                            showAllItemsForOrder === order._id && (
                              <button
                                className="text-[10px] text-purple-400 hover:text-purple-300 underline font-semibold cursor-pointer text-left self-start"
                                onClick={() => setShowAllItemsForOrder(null)}
                              >
                                Show less
                              </button>
                            )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-400 font-mono text-xs truncate max-w-[150px]">
                        {order.razorpayOrderId ? order.razorpayOrderId : order.stripePaymentIntentId || "N/A"}
                      </td>
                      <td className="px-5 py-4 text-center">
                        {isPaid ? (
                          <div className="flex gap-2 justify-center">
                            <button
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white rounded-lg text-xs font-semibold tracking-wide border border-white/5 transition-all cursor-pointer"
                              onClick={() => handleViewInvoice(order)}
                              title="View Invoice"
                            >
                              <FaDownload size={9} /> Invoice
                            </button>
                            <button
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-semibold tracking-wide border border-emerald-500/10 transition-all cursor-pointer"
                              onClick={() => navigate('/profile/downloads')}
                              title="Go to Downloads"
                            >
                              <FaDownload size={9} /> Download
                            </button>
                          </div>
                        ) : (
                          <button
                            className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
                            onClick={() => handleRetryPayment(order)}
                            disabled={processingOrderId === order._id}
                          >
                            {processingOrderId === order._id ? "Processing.." : order.status === "failed" ? "Retry" : "Pay Now"}
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/5 flex-wrap gap-4">
            <p className="text-xs text-gray-400">
              Showing <span className="font-semibold text-white">{indexOfFirstOrder + 1}</span> to{" "}
              <span className="font-semibold text-white">
                {Math.min(indexOfLastOrder, allOrders.length)}
              </span>{" "}
              of <span className="font-semibold text-white">{allOrders.length}</span> orders
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border border-white/10 hover:border-purple-500/30 hover:bg-white/5 disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:bg-transparent text-xs font-semibold tracking-wider uppercase transition-all duration-300 cursor-pointer text-white"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-all duration-300 cursor-pointer ${
                    currentPage === page
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/20"
                      : "border border-white/10 hover:border-purple-500/30 hover:bg-white/5 text-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-white/10 hover:border-purple-500/30 hover:bg-white/5 disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:bg-transparent text-xs font-semibold tracking-wider uppercase transition-all duration-300 cursor-pointer text-white"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      {invoiceModal.open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-pointer">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-3xl w-full max-w-4xl h-[90vh] flex flex-col shadow-[0_0_50px_rgba(124,58,237,0.1)] relative overflow-hidden font-sans">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/5 bg-[#141414]">
              <h3 className="text-xl font-bold text-white flex items-center gap-3 tracking-wide">
                <div className="w-2 h-8 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                INVOICE
              </h3>
              <button
                onClick={closeInvoiceModal}
                className="text-white/40 hover:text-white transition-all transform hover:rotate-90 cursor-pointer"
              >
                <IoIosCloseCircleOutline size={32} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 bg-[#0f0f0f] p-6 sm:p-10 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {invoiceModal.order && (() => {
                const order = invoiceModal.order;
                const userDetails = {
                  name: user?.fullName ? decryptData(user.fullName) : "Customer",
                  email: user?.email ? decryptData(user.email) : "",
                };

                let subtotal = 0;
                if (order.items && Array.isArray(order.items)) {
                  subtotal = order.items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
                }
                const tax = subtotal * 0.18;
                const discount = subtotal * 0.05;

                return (
                  <div className="relative max-w-3xl mx-auto min-h-[700px] bg-[#111] rounded-2xl overflow-hidden border border-white/5 shadow-2xl p-8 sm:p-12 cursor-pointer">

                    {/* Background Elements */}
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-pink-600/5 rounded-full blur-[80px] pointer-events-none translate-y-1/3 -translate-x-1/3"></div>

                    {/* Watermark */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                      <h1 className="text-[12rem] font-black text-white/5 -rotate-45 select-none transform origin-center tracking-widest">
                        YOYO
                      </h1>
                    </div>

                    {/* Content Re-Design */}
                    <div className="relative z-10 flex flex-col h-full">

                      {/* Top Bar: Brand & Status */}
                      <div className="flex justify-between items-start mb-12">
                        <div className="space-y-1">
                          <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600 mb-1 filter drop-shadow-lg">YOYO GAMES</h1>
                          <p className="text-gray-400 text-sm tracking-wider uppercase">Powered by VS Infotech.</p>
                        </div>
                        <div className="text-right">
                          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${order.status === 'paid' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'} backdrop-blur-sm`}>
                            <div className={`w-2 h-2 rounded-full ${order.status === 'paid' ? 'bg-emerald-400 animate-pulse' : 'bg-yellow-400'}`}></div>
                            <span className="text-xs font-bold uppercase tracking-wider">{order.status}</span>
                          </div>
                        </div>
                      </div>

                      {/* Invoice Information Grid */}
                      <div className="grid grid-cols-2 gap-12 mb-12">
                        {/* Bill To */}
                        <div>
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <span className="w-1 h-1 bg-purple-500 rounded-full"></span> Billed To
                          </h4>
                          <div className="space-y-1">
                            <h2 className="text-xl font-bold text-white">{userDetails.name}</h2>
                            <p className="text-sm text-gray-400 font-medium">{userDetails.email}</p>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="text-right">
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center justify-end gap-2">
                            Invoice Details <span className="w-1 h-1 bg-pink-500 rounded-full"></span>
                          </h4>
                          <div className="space-y-2">
                            {order.orderNumber && (
                              <div className="flex justify-end gap-4">
                                <span className="text-sm text-gray-500">Order No.</span>
                                <span className="text-sm font-mono font-bold text-white tracking-wider">{order.orderNumber}</span>
                              </div>
                            )}
                            <div className="flex justify-end gap-4">
                              <span className="text-sm text-gray-500">Invoice No.</span>
                              <span className="text-sm font-mono font-bold text-white tracking-wider">
                                {order.invoiceNumber ? `#${order.invoiceNumber}` : `#${order._id.slice(-8).toUpperCase()}`}
                              </span>
                            </div>
                            <div className="flex justify-end gap-4">
                              <span className="text-sm text-gray-500">Date Issued</span>
                              <span className="text-sm font-medium text-white">{new Date(order.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Decorative Divider */}
                      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8"></div>

                      {/* Items List */}
                      <div className="mb-8 flex-1">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Purchased Items</h4>
                        <div className="space-y-3">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="group relative flex items-center justify-between p-4 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl transition-all duration-300">
                              {/* Hover Glow */}
                              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none"></div>

                              <div className="flex items-center gap-4 relative z-10">
                                <div className="w-12 h-12 rounded-lg bg-[#222] border border-white/10 overflow-hidden shadow-lg group-hover:shadow-purple-500/20 transition-all">
                                  {item.game?.cover_image?.url ? (
                                    <img src={item.game?.cover_image?.url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-600">N/A</div>
                                  )}
                                </div>
                                <div>
                                  <h3 className="font-bold text-white text-sm group-hover:text-purple-300 transition-colors">{item.game?.title || "Unknown Game"}</h3>
                                  <p className="text-xs text-gray-500 capitalize">{item.platform}</p>
                                </div>
                              </div>

                              <div className="relative z-10 text-right">
                                <span className="block font-mono font-medium text-white">${Number(item.price).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Totals Section */}
                      <div className="flex justify-end">
                        <div className="w-full max-w-sm rounded-2xl bg-white/[0.03] border border-white/10 p-6 relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>

                          <div className="relative z-10 space-y-3">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">Subtotal</span>
                              <span className="text-gray-300 font-medium font-mono">${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">Tax <span className="text-[10px] bg-white/10 px-1 rounded ml-1">18%</span></span>
                              <span className="text-gray-300 font-medium font-mono">+${tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">Discount <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1 rounded ml-1">5%</span></span>
                              <span className="text-emerald-400 font-medium font-mono">-${discount.toFixed(2)}</span>
                            </div>

                            <div className="h-px bg-white/10 my-3"></div>

                            <div className="flex justify-between items-center">
                              <span className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Total </span>
                              <div className="text-right">
                                <span className="block text-2xl font-black text-white tracking-tight">${order?.amount?.toFixed(2)}</span>
                                <span className="block text-[10px] text-gray-500 mt-1">Include of all taxes</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Footer */}
                      <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-600">
                        <p>Thank you for shopping with YOYO!</p>
                        <p className="font-mono">Generated: {new Date().toLocaleTimeString()} • {new Date().toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-white/5 bg-[#141414] flex justify-end gap-4">
              <div className="flex gap-3">
                <button
                  onClick={closeInvoiceModal}
                  className="px-6 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all text-sm font-medium border border-white/5 hover:border-white/10 cursor-pointer"
                >
                  Close
                </button>
                <button
                  onClick={downloadFromModal}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all flex items-center gap-2 text-sm transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <FaDownload className="text-sm" /> <span>Download Invoice</span>
                </button>
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* Stripe Payment Modal */}
      <Modal
        open={openStripeModal}
        onClose={() => setOpenStripeModal(false)}
        aria-labelledby="stripe-checkout-modal"
        aria-describedby="stripe-payment-form"
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'rgba(15, 15, 35, 0.98)',
            border: '1px solid rgba(124, 58, 237, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(124, 58, 237, 0.2)',
            padding: '1.5rem',
          }}
        >
          <div className="text-white">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Complete Your Payment
            </h2>
            {clientSecret && orderData && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <StripeCheckoutForm
                  clientSecret={clientSecret}
                  orderData={orderData}
                  buyNow={true}
                  onSuccess={() => {
                    setOpenStripeModal(false);
                    setShowSuccessModal(true);
                  }}
                  onCancel={() => setOpenStripeModal(false)}
                />
              </Elements>
            )}
          </div>
        </div>
      </Modal>

      <OrderSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </>
  );
};

export default Orders;
